import _ from 'lodash';
import { t, Trans } from '@lingui/macro';

import Panel from '../../page-framework/panel/PanelDecorator';
import {
    PANEL_CATEGORIES,
    PANEL_PRIORITIES,
    STATUS_BREAKOUT_CHART_PANEL_PREVIEW_IMAGE,
} from '../../page-framework/panel/PanelRegistry';
import { panelPropDefaults, panelPropTypes } from '../../page-framework/panel/PanelPropTypes';
import FixedLabelContainer, { Row, Label, Content, Line } from '../../fixed-label-container/FixedLabelContainer';
import StatusBreakout from '../../charts/status-breakout/StatusBreakout';
import StatusBreakoutRow from '../../status-breakout/StatusBreakoutRow';
import { convertStatusToStatusCounts } from '../../charts/data-series';
import { accessPointFilterId, networkDeviceFilterId, timeRangeFilterId, useAccessPoint } from '../../filters';
import usePanelDataService from '../../../services/PanelDataService';

const title = t`Access Point Status`;

const empty = { trends: { status: { data: [] } } };

const dataSource = {
    url: '/query/table',
    params: {
        type: 'device',
        metrics: ['status', 'deviceInterfaceStatus', 'deviceInterfaceCounts', 'radioCounts'],
        trends: ['status', 'deviceInterfaceStatus', 'deviceInterfaceCounts', 'radioCounts'],
        renderAsEntity: true,
    },
};

function checkData(data) {
    const hasInterfaces = _.get(data, 'deviceInterfaceCounts.total') > 0;
    const hasRadios = _.get(data, 'radioCounts.total') > 0;
    const last = hasRadios ? 'radioCounts' : 'deviceInterfaceCounts';

    return [hasInterfaces, hasRadios, last];
}

function AccessPointStatusBreakoutPanel() {
    const accessPoint = useAccessPoint();
    const statusCounts = usePanelDataService(dataSource);
    const statusData = statusCounts?.data?.[0];

    if (!accessPoint || !statusCounts) return null;

    // We need to pass a Device filter (not AccessPoint) for the target page on the Interfaces status breakout link
    const filters = [{ name: networkDeviceFilterId, uuid: accessPoint?.uuid }];

    const overAllCounts = statusData ? convertStatusToStatusCounts('status', statusData) : empty;
    const [hasInterfaces, hasRadios, last] = checkData(statusData);

    return (
        <FixedLabelContainer>
            <Row>
                <Label>
                    <Trans>Overall Status</Trans>
                </Label>
                <Content>
                    <StatusBreakout
                        height={20}
                        noSelection
                        disableTimeAxis
                        metricName="status"
                        data={overAllCounts}
                        isStatus
                    />
                </Content>
            </Row>
            {(hasInterfaces || hasRadios) && <Line />}
            {hasInterfaces && (
                <Row>
                    <StatusBreakoutRow
                        noSelection
                        disableSelection
                        page="/interfaces"
                        title={<Trans>Interfaces</Trans>}
                        metric="deviceInterfaceCounts"
                        last={last}
                        data={statusData}
                        filters={filters}
                        excludedFilters={[accessPointFilterId]}
                        hideProblems
                    />
                </Row>
            )}
            {hasRadios && (
                <Row>
                    <StatusBreakoutRow
                        noSelection
                        disableSelection
                        page="/radios"
                        title={<Trans>Radios</Trans>}
                        metric="radioCounts"
                        last={last}
                        data={statusData}
                        hideProblems
                    />
                </Row>
            )}
        </FixedLabelContainer>
    );
}

AccessPointStatusBreakoutPanel.propTypes = panelPropTypes;

AccessPointStatusBreakoutPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    autoHeight: true,
    minContentHeight: 50,
};

export default Panel({
    typeAlias: 'AccessPointStatusBreakout',
    catalogEntry: {
        category: PANEL_CATEGORIES.ACCESS_POINT,
        priority: PANEL_PRIORITIES.STATUS,
        title,
        description: t`Status breakdown for a selected wireless access point (AP)`,
        previewImage: STATUS_BREAKOUT_CHART_PANEL_PREVIEW_IMAGE,
        ipm: true,
    },
    filters: {
        required: [timeRangeFilterId, accessPointFilterId],
        optional: [],
    },
})(AccessPointStatusBreakoutPanel);
